import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DialogPresets,
  type IMatSelectGroupOptions,
  LoaderPlaceholderComponent,
  matSelectGroupOptions,
  PipesModule,
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  type ITemplateContextOption,
  type ITemplateDefinition,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  getEnumValues,
  IGroup,
  ISelectOption,
} from '@principle-theorem/shared';
import { compact, first, isString, sortBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  map,
  startWith,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import {
  type IPopulatedTemplate,
  populateTemplate,
} from '../contextual-actions/interaction-actions/template-context-resolvers/mention-to-templates';
import { TemplateContextSelectorDialogComponent } from '../template-context-selector-dialog/template-context-selector-dialog.component';

@Component({
  selector: 'pr-template-selector',
  templateUrl: './template-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PipesModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    LoaderPlaceholderComponent,
  ],
})
export class TemplateSelectorComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  templateFilterCtrl = new TypedFormControl<string>('');
  templateCtrl = new TypedFormControl<ITemplateDefinition>();
  options$ = new ReplaySubject<
    IMatSelectGroupOptions<TemplateScope, ITemplateDefinition>
  >(1);
  filteredOptions$: Observable<
    IGroup<ISelectOption<ITemplateDefinition>, TemplateScope>[]
  >;
  contextOptions$ = new ReplaySubject<ITemplateContextOption[]>(1);
  loading$ = new BehaviorSubject<boolean>(true);
  resolving$ = new BehaviorSubject<boolean>(false);
  @Output() templateSelected = new EventEmitter<IPopulatedTemplate>();
  @Output() menuOpened = new EventEmitter<void>();
  @Input() resetOnSelection: boolean = false;
  @Input() displayMode: 'inline' | 'menuItem' = 'inline';
  @Input() iconName: string = 'text_snippet';
  @Input() tooltipText: string = 'Apply Template';
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  @Input()
  set templates(templates: ITemplateDefinition[]) {
    if (!templates) {
      return;
    }
    const options = getEnumValues(TemplateScope)
      .map((scope) => ({
        group: scope,
        items: sortBy(
          templates
            .filter((template) => template.scope === scope)
            .map((template) => ({
              label: template.name,
              value: template,
            })),
          'label'
        ),
      }))
      .filter((option) => option.items.length > 0);
    this.options$.next(matSelectGroupOptions(options));
    this.loading$.next(false);
  }

  @Input()
  set contextOptions(contextOptions: ITemplateContextOption[]) {
    if (contextOptions) {
      this.contextOptions$.next(contextOptions);
    }
  }

  constructor(
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar
  ) {
    this.templateCtrl.valueChanges
      .pipe(
        withLatestFrom(this.contextOptions$),
        switchMap(async ([template, contextOptions]) => {
          const selected = await this._selectContextOption(
            template,
            contextOptions
          );
          if (!selected) {
            return;
          }
          this.resolving$.next(true);
          try {
            const resolved = await selected.resolve();
            if (!resolved) {
              // resolve() -> undefined means the linked record is gone (per the
              // ITemplateContextOption.resolve() contract), not a load failure.
              this._snackBar.open(
                'This template is no longer available. The linked record may have been removed.'
              );
              return;
            }
            return await populateTemplate(
              template,
              resolved.context,
              resolved.scopeData
            );
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to resolve template context', error);
            this._snackBar.open('Unable to load template data');
            return;
          } finally {
            this.resolving$.next(false);
          }
        }),
        filterUndefined(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((template) => {
        this.templateSelected.emit(template);
        if (this.resetOnSelection) {
          this.templateFilterCtrl.setValue('');
          this.templateCtrl.reset(undefined, { emitEvent: false });
        }
      });

    this.filteredOptions$ = combineLatest([
      this.options$,
      validFormControlChanges$(this.templateFilterCtrl).pipe(startWith('')),
    ]).pipe(
      map(([options, search]) => this._getFilteredOptions(options, search))
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  closeMenu(): void {
    this.menuTrigger?.closeMenu();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement) {
      event.stopPropagation();
    }
  }

  focusSearchInput(): void {
    setTimeout(() => this.searchInput?.nativeElement?.focus());
  }

  private async _selectContextOption(
    template: ITemplateDefinition,
    contextOptions: ITemplateContextOption[]
  ): Promise<ITemplateContextOption | undefined> {
    const filteredContextOptions = contextOptions.filter(
      (option) => option.scope === template.scope
    );
    const defaultOption = first(filteredContextOptions);
    if (!defaultOption) {
      return;
    }
    if (filteredContextOptions.length === 1) {
      return defaultOption;
    }

    const selectedOption = await this._dialog
      .open<
        TemplateContextSelectorDialogComponent,
        ITemplateContextOption[],
        ITemplateContextOption
      >(
        TemplateContextSelectorDialogComponent,
        DialogPresets.small({
          data: filteredContextOptions,
        })
      )
      .afterClosed()
      .toPromise();

    return selectedOption;
  }

  private _getFilteredOptions(
    options: IMatSelectGroupOptions<TemplateScope, ITemplateDefinition>,
    search?: string
  ): IGroup<ISelectOption<ITemplateDefinition>, TemplateScope>[] {
    if (!search || !isString(search)) {
      return options.groups;
    }
    const searchValue = search?.trim().toLowerCase();
    return compact(
      options.groups.map((option) => {
        const filteredItems = option.items.filter((item) =>
          item.label.toLowerCase().includes(searchValue)
        );

        if (!filteredItems.length) {
          return;
        }

        return {
          ...option,
          items: filteredItems,
        };
      })
    );
  }
}
